import {Card, Table} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {
  Settlement,
  SettlementAcquirer,
  SettlementPayMethods,
  useOnUpdateAnalyticsSubscription,
  useSettlementsQuery,
  useStoresQuery
} from 'api/generated';
import {Hint, TableCard} from 'components';
import {PayoutStatusTag} from 'components/PayoutStatusTag';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {formatDateRange, formatIBAN, formatIntAmount} from 'lib/formaters';
import moment from 'moment-timezone';
import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import styled from 'styled-components';
import {acquirers, paymentMethodsEnum} from '../../../types';
import {SectionProps} from '../types';
import {PayoutActions} from './PayoutActions';
import {FilterQueryParams, PayoutFilters} from './PayoutFilters';
import {omitEmpty} from 'lib/omitEmpty';
import {NumberParam, QueryParamConfig, useQueryParam, useQueryParams} from 'use-query-params';
import {StringParam} from 'use-query-params';
import {SearchableSettlementFilterInput} from 'types/api';
import {PAYOUT_FILTERS} from 'lib/enums';
import {FilterList} from 'components/FilterList';
import {isDefined} from 'lib/utils';
import deepEqual from 'deep-equal';
import {useDebouncedCallback} from 'use-debounce';

const Amount = styled.span`
  color: rgba(0, 0, 0, 0.85);
  font-weight: 500;
`;

export const queryFiltersSchema = {
  storeId: StringParam
};

export const NO_STORE_VALUE = 'no-store';

const buildFilter = (
  queryFilters: {[key: string]: any},
  accountId?: string
): SearchableSettlementFilterInput => {
  const filter: SearchableSettlementFilterInput = {};
  if (accountId) {
    filter.accountId = {match: accountId};
  }
  Object.entries(queryFilters).forEach(([key, value]) => {
    if (!isDefined(value, {allowEmptyString: false})) return;
    if (key === 'storeId' && value === NO_STORE_VALUE) {
      (filter as any)[key] = {exists: false};
    } else {
      (filter as any)[key] = {eq: value};
    }
  });
  return filter;
};

export const PayoutSettings: React.FC<SectionProps> = ({account, cardProps}) => {
  const [size = 20, setSize] = useQueryParam<number | undefined>(
    'size',
    NumberParam as QueryParamConfig<number | undefined>
  );
  const [from = 0, setFrom] = useQueryParam<number | undefined>(
    'from',
    NumberParam as QueryParamConfig<number | undefined>
  );

  const {isAdmin} = useAccessLevel();
  const {accountId} = useParams<{accountId: string}>();
  const [queryFilters, setQueryFilters] = useQueryParams<{[key: string]: any}>(queryFiltersSchema);
  const filter = buildFilter(queryFilters, accountId);

  const {data, loading, previousData, refetch} = useSettlementsQuery({
    variables: {from, size, filter}
  });

  const {data: storesData} = useStoresQuery({
    skip: !accountId,
    variables: {
      accountId
    }
  });

  const stores = storesData?.stores.items || [];

  const update = useDebouncedCallback(refetch, 5000);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useOnUpdateAnalyticsSubscription({
    onSubscriptionData: () => update()
  });

  if (!account) return <Card {...cardProps} title="Payout settings" />;

  const payload = data ?? previousData;
  const payouts = payload?.settlements.items || [];
  const total = payload?.settlements.total || 0;

  const handleFiltersChange = (values: FilterQueryParams) => {
    if (deepEqual(omitEmpty(values), omitEmpty(queryFilters))) {
      return refetch();
    }
    setFrom(undefined, 'pushIn');
    setQueryFilters(values, 'pushIn');
  };

  const handleCloseTag = (key: string) => setQueryFilters({...queryFilters, [key]: null}, 'pushIn');

  const availableFilters: Record<string, string> = {...PAYOUT_FILTERS};

  const columns: Array<ColumnProps<Settlement>> = [
    {
      title: 'Amount',
      dataIndex: 'amount',
      align: 'right',
      className: 'minimized-cell',
      render(_, {netVolume, grossVolume, settleGross}) {
        if (settleGross) {
          return <Amount>{formatIntAmount(grossVolume)}</Amount>;
        }
        return <Amount>{formatIntAmount(netVolume)}</Amount>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_, payout) => <PayoutStatusTag payout={payout} />
    },
    {
      title: () => (
        <Hint
          title="Period"
          message="The date of the first and last payment included in the payout."
        />
      ),
      dataIndex: 'period',
      render: (_, {periodStartAt, periodEndAt}) => formatDateRange(periodStartAt, periodEndAt, 'LL')
    },
    {
      title: () => (
        <Hint
          title="Completion date"
          message="You can expect the bank transfer for this payout after this date."
        />
      ),
      dataIndex: 'scheduledAt',
      render: (scheduledAt: number) => moment.unix(scheduledAt).format('LL')
    },
    {
      title: 'IBAN',
      dataIndex: 'iban',
      render: (iban: string) => formatIBAN(iban, true)
    },
    {
      title: 'Payment method',
      dataIndex: 'payMethod',
      render: (payMethod: SettlementPayMethods) => paymentMethodsEnum[payMethod || 'card']
    },
    ...(isAdmin
      ? [
          {
            title: 'Acquirer',
            dataIndex: 'acquirer',
            render: (acquirer: SettlementAcquirer) => acquirers[acquirer]
          }
        ]
      : []),
    {
      key: 'actions',
      width: 100,
      fixed: 'right',
      align: 'right',
      render: (_, payout) => <PayoutActions payout={payout} />
    }
  ];

  return (
    <>
      {stores.length > 1 && (
        <>
          <PayoutFilters
            onChange={handleFiltersChange}
            queryFilters={queryFilters}
            availableFilters={availableFilters}
            accountId={accountId}
            stores={stores}
          />
          <FilterList
            accountId={accountId}
            queryFilters={queryFilters}
            filters={availableFilters}
            onClose={handleCloseTag}
            defaultQuery="?tab=payouts"
          />
        </>
      )}
      <TableCard {...cardProps} title="Payouts history">
        <Table
          rowKey="id"
          dataSource={payouts}
          columns={columns as any}
          loading={loading}
          pagination={{
            total,
            pageSize: size,
            hideOnSinglePage: true,
            current: from / size + 1,
            onChange: (page, size = 0) => {
              setSize(size);
              setFrom((page - 1) * size);
            }
          }}
        />
      </TableCard>
    </>
  );
};
